// Operations → Now. The default view (C1, §7.1 LOCKED).
//
// C1 SAYS THE DEFAULT IS "NOW", NOT "OVERVIEW"
// ============================================
// `operations.html` opened on Overview. §7.1 is locked and states the default
// view is Now, and §3.8 gives `/operations` → `OperationsPage` the new home
// "Operations → Now". This module is what the index route renders, so the
// default *view* is Now as required — and the X-axis label in `shell/areas.js`
// (`XNAV.operations[0]`) reads "Now" as well, so the tab and the surface it
// opens on agree.
//
// WHAT "NOW" MEANS HERE
// =====================
// §7 is about what the company is doing *right now*, so this reads the four
// endpoints that answer that and nothing else: whether the agents are running,
// whether anything has tripped a breaker, whether the integrations are up, and
// how the two business units are doing. Cost accounting is deliberately absent —
// A1 puts it under Settings → Infrastructure, and §7 is not an accounting
// surface.
//
// NO TINY TIM
// ===========
// `OperationsPage` imports `TinyTimCostTile` and `TinyTimToolHistory` and renders
// both. This surface imports neither. That is the whole of the decoupling asked
// for in §3.8, done additively: the old page keeps both and keeps working.
import React from "react";

import PageHead from "../../components/PageHead";
import Panel, { Figure, Rows, StatusRows, tone } from "./Panel";
import { READY, metric, useEndpoint } from "./useEndpoint";

function list(payload, ...keys) {
  for (const k of keys) {
    const v = payload?.[k];
    if (Array.isArray(v)) return v;
  }
  return Array.isArray(payload) ? payload : [];
}

export default function Now() {
  const agents = useEndpoint("/agents/status");
  const breaker = useEndpoint("/circuit-breaker/status");
  const integrations = useEndpoint("/integrations/health");
  const units = useEndpoint("/bu/health");

  const agentRows = list(agents.data, "agents", "jobs");
  const integrationRows = list(integrations.data, "integrations", "services");
  const unitRows = list(units.data, "business_units", "units");

  return (
    <>
      <PageHead
        title="Now"
        subtitle="What the company is doing right now."
      />

      <div className="grid op-grid">
        <Panel
          title="Agents"
          state={agents}
          empty={agentRows.length === 0}
          note="No agent has reported a run. /api/agents/status returned no entries."
        >
          {/* a status is a state, so it carries a dot; `tone` leaves anything
              it does not recognise as unknown rather than reading it green */}
          <StatusRows
            items={agentRows.slice(0, 12).map((a) => [
              String(a?.job_id ?? a?.name ?? a?.id ?? "agent"),
              String(a?.status ?? a?.state ?? "—"),
            ])}
          />
        </Panel>

        <Panel
          title="Circuit breaker"
          state={breaker}
          empty={breaker.status === READY && !breaker.data}
          note="/api/circuit-breaker/status returned nothing."
        >
          <Figure
            label="State"
            value={metric(breaker.data?.state ?? breaker.data?.status)}
            tone={tone(breaker.data?.state ?? breaker.data?.status)}
            detail={breaker.data?.is_tripped ? "tripped" : "no trips recorded"}
          />
        </Panel>

        <Panel
          title="Integrations"
          state={integrations}
          empty={integrationRows.length === 0}
          note="/api/integrations/health reported no integrations."
        >
          <StatusRows
            items={integrationRows.slice(0, 12).map((i) => [
              String(i?.service ?? "integration"),
              String(i?.status ?? i?.healthy ?? "—"),
            ])}
          />
        </Panel>

        <Panel
          title="Business units"
          state={units}
          empty={unitRows.length === 0}
          note="/api/bu/health reported no business units."
        >
          <Rows
            items={unitRows.slice(0, 12).map((u) => [
              String(u?.business_unit_id ?? u?.bu ?? u?.name ?? "unit"),
              String(u?.status ?? u?.health ?? "—"),
            ])}
          />
        </Panel>
      </div>
    </>
  );
}
